/**
 * E45 — i18n provider. Default locale DE (German-first product); EN secondary.
 * Locale persists in localStorage ("pv.locale") and the header switcher is a
 * real control (the previous `🌐 EN` span was a dead element — worse than none).
 */
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { MESSAGES, type Locale, type MessageKey } from "./messages.js";

export type { Locale, MessageKey };

const LS_LOCALE = "pv.locale";

export function getStoredLocale(): Locale {
  try {
    const v = localStorage.getItem(LS_LOCALE);
    if (v === "de" || v === "en") return v;
  } catch {
    /* storage unavailable → default */
  }
  return "de";
}

interface LocaleCtxValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
}

const LocaleCtx = createContext<LocaleCtxValue>({ locale: "de", setLocale: () => undefined });

export function LocaleProvider({ children }: { children: ReactNode }): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(getStoredLocale());
  const value = useMemo<LocaleCtxValue>(
    () => ({
      locale,
      setLocale: (l) => {
        try {
          localStorage.setItem(LS_LOCALE, l);
        } catch {
          /* non-fatal */
        }
        setLocaleState(l);
      },
    }),
    [locale],
  );
  return <LocaleCtx.Provider value={value}>{children}</LocaleCtx.Provider>;
}

export function useLocale(): LocaleCtxValue {
  return useContext(LocaleCtx);
}

/** Translate for an explicit locale (non-hook contexts). {var} placeholders are substituted. */
export function tFor(locale: Locale, key: MessageKey, vars?: Record<string, string | number>): string {
  let s: string = MESSAGES[locale][key] ?? MESSAGES.de[key] ?? key;
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v));
  return s;
}

/** Hook: current-locale translator. */
export function useT(): (key: MessageKey, vars?: Record<string, string | number>) => string {
  const { locale } = useLocale();
  return (key, vars) => tFor(locale, key, vars);
}
